import { supabase } from './supabaseClient';

export interface Product {
    id: number;
    category: string;
    name: string;
    description: string;
    price: string;
    images?: string[];
    image: string;
    // Flatted fields to match new DB schema
    code: string;
    material: string;
    dimensions: string;
}

export interface Category {
    id: number;
    name: string;
}

// --- PRODUCT OPERATIONS ---

export const getAllProducts = async (): Promise<Product[]> => {
    const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('id', { ascending: false });

    if (error) {
        console.error('Error fetching products:', error);
        return [];
    }
    return data || [];
};

export const getProductById = async (id: number): Promise<Product | undefined> => {
    const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

    if (error) {
        console.error('Error fetching product:', error);
        return undefined;
    }
    return data;
};

export const addProduct = async (product: Omit<Product, 'id'>): Promise<Product | null> => {
    // Sync images[0] to image if needed
    const payload = { ...product };
    if (payload.images && payload.images.length > 0 && !payload.image) {
        payload.image = payload.images[0];
    }

    // We assume the DB generates the ID
    const { data, error } = await supabase
        .from('products')
        .insert([payload])
        .select()
        .single();

    if (error) {
        console.error('CRITICAL: Error adding product:', error);
        alert(`Failed to add product: ${error.message} (Code: ${error.code})`);
        return null;
    }
    return data;
};

export const updateProduct = async (id: number, updates: Partial<Product>): Promise<Product | null> => {
    const payload = { ...updates };
    if (payload.images && payload.images.length > 0) {
        payload.image = payload.images[0];
    }

    const { data, error } = await supabase
        .from('products')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

    if (error) {
        console.error('Error updating product:', error);
        alert(`Failed to update product: ${error.message}`);
        return null;
    }
    return data;
};

export const deleteProduct = async (id: number): Promise<boolean> => {
    const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

    if (error) {
        console.error('Error deleting product:', error);
        return false;
    }
    return true;
};

// --- CATEGORY OPERATIONS ---

export const getCategories = async (): Promise<Category[]> => {
    const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('id', { ascending: true });

    if (error) {
        console.error('Error fetching categories:', error);
        return [];
    }
    return data || [];
};

export const addCategory = async (name: string): Promise<Category | null> => {
    const { data, error } = await supabase
        .from('categories')
        .insert([{ name }])
        .select()
        .single();

    if (error) {
        console.error('Error adding category:', error);
        return null;
    }
    return data;
};

export const deleteCategory = async (id: number): Promise<boolean> => {
    const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

    if (error) {
        console.error('Error deleting category:', error);
        return false;
    }
    return true;
};
// --- INQUIRY OPERATIONS ---

export interface Inquiry {
    id: number;
    name: string;
    email: string;
    phone: string;
    type: string;
    budget: string;
    message: string;
    status: 'new' | 'read' | 'contacted' | 'closed';
    created_at: string;
}

export const getInquiries = async (): Promise<Inquiry[]> => {
    const { data, error } = await supabase
        .from('inquiries')
        .select('*')
        .order('created_at', { ascending: false });

    if (error) {
        console.error('Error fetching inquiries:', error);
        return [];
    }
    return data || [];
};

export const addInquiry = async (inquiry: Omit<Inquiry, 'id' | 'status' | 'created_at'>): Promise<boolean> => {
    const { error } = await supabase
        .from('inquiries')
        .insert([inquiry]);

    if (error) {
        console.error('Error adding inquiry:', error);
        return false;
    }
    return true;
};

export const updateInquiryStatus = async (id: number, status: string): Promise<boolean> => {
    const { error } = await supabase
        .from('inquiries')
        .update({ status })
        .eq('id', id);

    if (error) {
        console.error('Error updating inquiry status:', error);
        return false;
    }
    return true;
};

export const updateInquiry = async (id: number, updates: Partial<Inquiry>): Promise<boolean> => {
    const { error } = await supabase
        .from('inquiries')
        .update(updates)
        .eq('id', id);

    if (error) {
        console.error('Error updating inquiry:', error);
        return false;
    }
    return true;
};

export const deleteInquiry = async (id: number): Promise<boolean> => {
    const { error } = await supabase
        .from('inquiries')
        .delete()
        .eq('id', id);

    if (error) {
        console.error('Error deleting inquiry:', error);
        return false;
    }
    return true;
};
